// @/components/auth/AuthErrorBoundary.jsx
// Error boundary for auth flows — cleans up auth state on auth-related errors

import React from 'react';
import ErrorDisplay from '@/components/reusable/ErrorDisplay';
import { clearAuthStorage } from '@/services/auth';

class AuthErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: props.error || null, resetKey: 0 };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  static getDerivedStateFromProps(props, state) {
    if (props.error && !state.hasError) {
      return { hasError: true, error: props.error };
    }
    return null;
  }

  componentDidCatch(error, errorInfo) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Auth error caught by boundary:', error);
      console.log('Component stack:', errorInfo.componentStack);
    }

    const msg = error.message?.toLowerCase() || '';
    if (msg.includes('auth') || msg.includes('login') || msg.includes('token') || msg.includes('keycloak')) {
      clearAuthStorage().catch(() => { });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, resetKey: this.state.resetKey + 1 });
  };

  handleRelogin = async () => {
    await clearAuthStorage().catch(() => { });
    window.location.href = `/?v=${Date.now()}`;
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorDisplay
          title="Authentication Error"
          message={this.state.error?.message || "There was a problem with your session. This may be due to an expired login or network issue."}
          primaryAction={{ label: "Log In Again", onClick: this.handleRelogin }}
          secondaryAction={{ label: "Try Again", onClick: this.handleReset }}
        />
      );
    }
    return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
  }
}

export default AuthErrorBoundary;
